import styled from "styled-components";
import { ChatDesktopMode } from "./chatPresentation";
import {
  CHAT_DESKTOP_PANEL_WIDTH,
  CHAT_DESKTOP_PINNED_PANEL_WIDTH,
} from "./constants";

export const PersistentSurface = styled.div`
  display: contents;
`;

export const DesktopSurface = styled.aside<{
  $mode: ChatDesktopMode;
  $pinned: boolean;
}>`
  grid-area: chat-surface;
  position: ${({ $mode }) => ($mode === "page" ? "relative" : "fixed")};
  inset: ${({ $mode }) => ($mode === "page" ? "auto" : "0 0 0 auto")};
  z-index: ${({ $mode, $pinned }) =>
    $mode === "panel" ? ($pinned ? 2 : 10) : 1};
  display: flex;
  flex: 1;
  width: ${({ $mode, $pinned }) => {
    if ($mode === "page") return "100%";
    if ($pinned) return CHAT_DESKTOP_PINNED_PANEL_WIDTH;
    return `min(${CHAT_DESKTOP_PANEL_WIDTH}, calc(100vw - 1rem))`;
  }};
  height: 100%;
  min-width: 0;
  min-height: 0;
  flex-direction: column;
  overflow: hidden;
  background: var(--background);
  color: var(--text-low-pure);
  opacity: ${({ $mode }) => (["page", "panel"].includes($mode) ? 1 : 0)};
  visibility: ${({ $mode }) =>
    ["page", "panel"].includes($mode) ? "visible" : "hidden"};
  pointer-events: ${({ $mode }) =>
    ["page", "panel"].includes($mode) ? "auto" : "none"};
  transform: ${({ $mode }) => {
    if (["page", "panel"].includes($mode))
      return "translate3d(0, 0, 0) scale(1)";
    if ($mode === "minimized") return "translate3d(0, 0, 0) scale(0.08)";
    return "translate3d(100%, 0, 0) scale(1)";
  }};
  transform-origin: calc(100% - 3.375rem) calc(100% - 3.375rem);
  box-shadow: ${({ $mode, $pinned }) =>
    $mode === "panel" && !$pinned
      ? "-0.5rem 0 2rem var(--shadow-panel), -0.125rem 0 0.5rem var(--shadow-panel)"
      : "none"};
  transition: ${({ $mode }) =>
    ["page", "panel"].includes($mode)
      ? "width 260ms ease, transform 420ms cubic-bezier(0.22, 1, 0.36, 1), opacity 260ms ease, visibility 0s"
      : "width 260ms ease, transform 420ms cubic-bezier(0.4, 0, 0.2, 1), opacity 260ms ease 80ms, visibility 0s linear 420ms"};
  will-change: ${({ $mode }) =>
    ["panel", "minimized"].includes($mode)
      ? "width, transform, opacity"
      : "auto"};

  ${({ $mode }) =>
    $mode === "panel" &&
    `
    border-left: 0.0625rem solid var(--border-muted);
  `}

  @media (prefers-reduced-motion: reduce) {
    transform: none;
    transition:
      opacity 160ms linear,
      visibility
        ${({ $mode }) =>
          ["page", "panel"].includes($mode) ? "0s" : "0s linear 160ms"};
  }
`;

export const VisuallyHidden = styled.span`
  position: absolute;
  width: 0.0625rem;
  height: 0.0625rem;
  padding: 0;
  border: 0;
  margin: -0.0625rem;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`;

export const TitleGroup = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 0.5rem;
`;

export const PageActions = styled.div`
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  flex-wrap: nowrap;
  gap: 0.25rem;
`;

export const Title = styled.h1`
  min-width: 0;
  overflow: hidden;
  color: ${({ theme }) => theme.colors.textHeader};
  font-size: 1.125rem;
  font-weight: 700;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:focus-visible {
    border-radius: 0.25rem;
    outline: 0.1875rem solid var(--primary-action);
    outline-offset: 0.25rem;
  }
`;

export const HeaderIconControl = styled.span<{ $onPrimary?: boolean }>`
  display: inline-flex;
  flex: 0 0 auto;
  color: ${({ $onPrimary, theme }) =>
    $onPrimary ? "var(--text-high-pure)" : theme.colors.textHeader};

  && > button {
    display: inline-flex;
    width: 2.75rem !important;
    min-width: 2.75rem !important;
    height: 2.75rem !important;
    min-height: 2.75rem !important;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 0;
    border-radius: 50% !important;
    background: transparent;
    color: inherit;
    box-shadow: inset 0 0 0 0.0625rem transparent;
    transition:
      background-color 140ms ease,
      box-shadow 140ms ease,
      color 140ms ease,
      transform 120ms ease;

    &:hover {
      background: ${({ $onPrimary, theme }) =>
        $onPrimary
          ? "color-mix(in srgb, var(--text-high-pure) 16%, transparent)"
          : theme.title === "dark"
            ? "#202020"
            : theme.colors.navbarHoverItem};
      box-shadow: ${({ $onPrimary, theme }) =>
        $onPrimary || theme.title === "dark"
          ? "none"
          : "inset 0 0 0 0.0625rem var(--chat-footer-border)"};
      color: ${({ $onPrimary, theme }) =>
        $onPrimary ? "var(--text-high-pure)" : theme.colors.textSecondary};
    }

    &:focus-visible {
      outline: 0.125rem solid currentColor;
      outline-offset: 0.125rem;
    }

    &:active {
      background: ${({ $onPrimary, theme }) =>
        $onPrimary
          ? "color-mix(in srgb, var(--text-high-pure) 28%, transparent)"
          : theme.title === "dark"
            ? "#202020"
            : theme.colors.navbarHoverItem};
      transform: scale(0.9);
    }
  }
`;

export const HeaderActionIcon = styled.span<{
  $icon: string;
  $enlargedMask?: boolean;
  $square?: boolean;
}>`
  display: inline-block;
  width: ${({ $square }) => ($square ? "1.4375rem" : "1.8125rem")};
  height: ${({ $square }) => ($square ? "1.4375rem" : "1.1875rem")};
  background-color: currentColor;
  mask: url(${({ $icon }) => $icon}) center /
    ${({ $enlargedMask }) => ($enlargedMask ? "200%" : "contain")} no-repeat;
  -webkit-mask: url(${({ $icon }) => $icon}) center /
    ${({ $enlargedMask }) => ($enlargedMask ? "200%" : "contain")} no-repeat;
`;

export const PanelHeader = styled.header`
  display: flex;
  min-height: 3.75rem;
  flex: 0 0 auto;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.5rem 0.75rem;
  background: ${({ theme }) =>
    theme.title === "dark" ? "#396bc9" : "#004992"};
  color: var(--text-high-pure);
`;

export const PanelTitle = styled.h2`
  min-width: 0;
  overflow: hidden;
  margin: 0;
  font-size: clamp(0.75rem, 2.5vw, 1rem);
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:focus-visible {
    border-radius: 0.25rem;
    outline: 0.1875rem solid var(--text-high-pure);
    outline-offset: 0.25rem;
  }
`;

export const PanelActions = styled.div`
  display: flex;
  flex: 0 0 auto;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: flex-end;
  gap: 0.375rem;

  && > button,
  && > span > button {
    display: inline-flex;
    width: 2.75rem !important;
    min-width: 2.75rem !important;
    height: 2.75rem !important;
    min-height: 2.75rem !important;
    padding: 0;
    border: 0;
    border-radius: 50% !important;
    background: transparent;
    color: var(--text-high-pure);
    align-items: center;
    justify-content: center;
    transition:
      background-color 120ms ease,
      transform 100ms ease;

    &:hover {
      background: color-mix(in srgb, var(--text-high-pure) 16%, transparent);
    }

    &:focus-visible {
      outline: 0.125rem solid var(--text-high-pure);
      outline-offset: 0.125rem;
    }

    &:active {
      border-radius: 50% !important;
      background: color-mix(in srgb, var(--text-high-pure) 28%, transparent);
      transform: scale(0.9);
    }

    &[aria-disabled="true"] {
      cursor: not-allowed !important;
      opacity: 0.55;

      &:hover,
      &:active {
        background: transparent;
        transform: none;
      }
    }
  }
`;

export const PanelActionIcon = styled.img`
  width: 1.125rem;
  height: 1.125rem;
  object-fit: contain;
`;

export const PanelButtonTarget = styled.span`
  position: relative;
  display: inline-flex;
`;

export const MinimizeGlyph = styled.span`
  display: inline-flex;
  width: 1.875rem;
  height: 1.875rem;
  align-items: center;
  justify-content: center;
  font-size: 1.875rem;
  line-height: 1;
`;

export const MessengerGrid = styled.div<{ $panel: boolean }>`
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: minmax(0, 1fr);
  flex: 1;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  border-top: ${({ $panel }) =>
    $panel ? "0" : "0.0625rem solid var(--border-muted)"};
`;

export const SplitMessengerGrid = styled.div<{
  $collapsed: boolean;
  $panel: boolean;
  $visible: boolean;
}>`
  grid-area: 1 / 1;
  display: ${({ $panel }) => ($panel ? "grid" : "flex")};
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: minmax(0, 1fr);
  min-width: 0;
  min-height: 0;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  visibility: ${({ $visible }) => ($visible ? "visible" : "hidden")};
  pointer-events: ${({ $visible }) => ($visible ? "auto" : "none")};
  transition: opacity 160ms ease;

  @media (prefers-reduced-motion: reduce) {
    transition: opacity 120ms linear;
  }
`;

export const ConversationList = styled.section<{
  $active: boolean;
  $collapsed: boolean;
  $panel: boolean;
}>`
  grid-area: ${({ $panel }) => ($panel ? "1 / 1" : "auto")};
  display: flex;
  width: ${({ $collapsed, $panel }) =>
    $panel ? "100%" : $collapsed ? "0" : "clamp(15rem, 32%, 28rem)"};
  flex: 0 0 auto;
  min-width: 0;
  min-height: 0;
  flex-direction: column;
  overflow: hidden;
  border-right: ${({ $collapsed, $panel }) =>
    $collapsed || $panel ? "0" : "0.0625rem solid var(--border-muted)"};
  opacity: ${({ $active, $collapsed, $panel }) =>
    !$active || (!$panel && $collapsed) ? 0 : 1};
  visibility: ${({ $active, $collapsed, $panel }) =>
    !$active || (!$panel && $collapsed) ? "hidden" : "visible"};
  pointer-events: ${({ $active, $collapsed, $panel }) =>
    !$active || (!$panel && $collapsed) ? "none" : "auto"};
  transform: translate3d(
    ${({ $active, $collapsed, $panel }) => {
      if ($panel) return $active ? "0" : "-1.5rem";
      return $collapsed ? "-0.75rem" : "0";
    }},
    0,
    0
  );
  transform-origin: left center;
  transition:
    width 360ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity 220ms ease,
    transform 360ms cubic-bezier(0.22, 1, 0.36, 1),
    visibility
      ${({ $active, $collapsed, $panel }) =>
        !$active || (!$panel && $collapsed) ? "0s linear 360ms" : "0s"};
  will-change: width, transform, opacity;

  @media (prefers-reduced-motion: reduce) {
    transform: none;
    transition:
      opacity 120ms linear,
      visibility
        ${({ $active, $collapsed, $panel }) =>
          !$active || (!$panel && $collapsed) ? "0s linear 120ms" : "0s"};
  }
`;

export const ConversationListHeader = styled.header`
  display: flex;
  min-height: 4.5rem;
  flex: 0 0 4.5rem;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem 0.75rem 0.75rem 1rem;
  background: ${({ theme }) =>
    theme.title === "dark" ? "#396bc9" : "#004992"};
  color: var(--text-high-pure);
`;

export const ConversationListTitle = styled.h2`
  min-width: 0;
  overflow: hidden;
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ConversationListTarget = styled.section`
  flex: 1;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
`;

export const MessageLog = styled.section<{ $active: boolean; $panel: boolean }>`
  grid-area: ${({ $panel }) => ($panel ? "1 / 1" : "auto")};
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  opacity: ${({ $active }) => ($active ? 1 : 0)};
  visibility: ${({ $active }) => ($active ? "visible" : "hidden")};
  pointer-events: ${({ $active }) => ($active ? "auto" : "none")};
  transform: translate3d(
    ${({ $active, $panel }) => ($panel && !$active ? "1.5rem" : "0")},
    0,
    0
  );
  transition:
    opacity 220ms ease,
    transform 360ms cubic-bezier(0.22, 1, 0.36, 1),
    visibility ${({ $active }) => ($active ? "0s" : "0s linear 360ms")};
  will-change: transform, opacity;

  @media (prefers-reduced-motion: reduce) {
    transform: none;
    transition:
      opacity 120ms linear,
      visibility ${({ $active }) => ($active ? "0s" : "0s linear 120ms")};
  }
`;

export const MinimizedControlDock = styled.div<{ $visible: boolean }>`
  position: fixed;
  right: 1.5rem;
  bottom: 1.5rem;
  z-index: 8;
  display: block;
  width: 3.75rem;
  height: 3.75rem;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  visibility: ${({ $visible }) => ($visible ? "visible" : "hidden")};
  pointer-events: ${({ $visible }) => ($visible ? "auto" : "none")};
  transform: translate3d(
      0,
      ${({ $visible }) => ($visible ? "0" : "0.75rem")},
      0
    )
    scale(${({ $visible }) => ($visible ? 1 : 0.9)});
  transition:
    opacity 180ms ease ${({ $visible }) => ($visible ? "320ms" : "0s")},
    transform 300ms cubic-bezier(0.22, 1, 0.36, 1)
      ${({ $visible }) => ($visible ? "280ms" : "0s")},
    visibility ${({ $visible }) => ($visible ? "0s" : "0s linear 300ms")};

  > button {
    width: 3.75rem !important;
    min-width: 3.75rem !important;
    height: 3.75rem !important;
    min-height: 3.75rem !important;
    padding: 0;
    border-radius: 50% !important;
  }

  @media (prefers-reduced-motion: reduce) {
    transform: none;
    transition:
      opacity 160ms linear,
      visibility ${({ $visible }) => ($visible ? "0s" : "0s linear 160ms")};
  }

  @media screen and (max-width: 960px) {
    display: none;
  }
`;

export const MinimizedButtonContent = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;

  > [data-testid="chat-unread-badge"] {
    top: -1rem;
    right: auto;
    left: -1rem;
  }

  img {
    width: 2.25rem;
    height: auto;
    object-fit: contain;
  }
`;

export const MobileRoutePresentation = styled.div`
  display: none;
`;

export const StatusLayer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 1.5rem;
  background: var(--background);
  color: var(--text-low-pure);
  text-align: center;
`;

export const UnavailableCard = styled.div`
  display: flex;
  width: min(100%, 30rem);
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;

  strong {
    font-size: 1.125rem;
  }

  > span {
    color: var(--text-low-pure);
    line-height: 1.5;
  }

  > small {
    max-width: 28rem;
    color: var(--text-low-pure);
    line-height: 1.4;
  }
`;

export const StatusActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.75rem;
`;

export const MobileOverlay = styled.section<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 20;
  display: flex;
  height: 100vh;
  height: 100dvh;
  min-width: 0;
  min-height: 0;
  flex-direction: column;
  overflow: hidden;
  padding-top: env(safe-area-inset-top, 0px);
  padding-bottom: env(safe-area-inset-bottom, 0px);
  background: var(--background);
  color: var(--text-low-pure);
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  visibility: ${({ $open }) => ($open ? "visible" : "hidden")};
  pointer-events: ${({ $open }) => ($open ? "auto" : "none")};
  transform: translate3d(${({ $open }) => ($open ? "0" : "100%")}, 0, 0);
  transition:
    transform 180ms ease,
    opacity 180ms ease,
    visibility 180ms ease;
  will-change: transform;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const MobileHeader = styled(PanelHeader)`
  position: relative;
  z-index: 2;
  padding-right: max(0.75rem, env(safe-area-inset-right, 0px));
  padding-left: max(0.75rem, env(safe-area-inset-left, 0px));

  &::after {
    position: absolute;
    right: 0;
    bottom: -0.0625rem;
    left: 0;
    height: 0.125rem;
    background: ${({ theme }) =>
      theme.title === "dark" ? "#396bc9" : "#004992"};
    content: "";
    pointer-events: none;
  }
`;

export const MobileTitle = styled(PanelTitle)`
  font-size: 1.125rem;
  font-weight: 700;
`;

export const MobileTargets = styled.div<{ $ready: boolean }>`
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: minmax(0, 1fr);
  flex: 1;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  opacity: ${({ $ready }) => ($ready ? 1 : 0)};
  pointer-events: ${({ $ready }) => ($ready ? "auto" : "none")};
`;

export const MobileEmbeddedTarget = styled.section<{ $active: boolean }>`
  grid-area: 1 / 1;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  opacity: ${({ $active }) => ($active ? 1 : 0)};
  visibility: ${({ $active }) => ($active ? "visible" : "hidden")};
  pointer-events: ${({ $active }) => ($active ? "auto" : "none")};
  transform: translate3d(${({ $active }) => ($active ? "0" : "1rem")}, 0, 0);
  transition:
    opacity 160ms ease,
    transform 220ms cubic-bezier(0.22, 1, 0.36, 1),
    visibility ${({ $active }) => ($active ? "0s" : "0s linear 220ms")};

  &:focus-visible {
    outline: 0.1875rem solid var(--primary-action);
    outline-offset: -0.1875rem;
  }

  @media (prefers-reduced-motion: reduce) {
    transform: none;
    transition:
      opacity 120ms linear,
      visibility ${({ $active }) => ($active ? "0s" : "0s linear 120ms")};
  }
`;

export const MobileBody = styled.div`
  position: relative;
  display: flex;
  flex: 1;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
`;

export const FocusSentinel = styled.span`
  position: fixed;
  width: 0.0625rem;
  height: 0.0625rem;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
`;
